import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import { useCart } from '../context/CartContext';
import { FaTruck, FaInfoCircle } from 'react-icons/fa';

function ProductDetails() {
  const { id } = useParams();
  const { addToCart, cart } = useCart();
  const safeCart = Array.isArray(cart) ? cart : [];

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  /* ---------------- FETCH PRODUCT ---------------- */
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `https://apimyshop.myidealtd.co.uk/api/products/${id}`
        );

        // SUPPORT BOTH API SHAPES
        const data = res.data?.product || res.data;

        console.log('ProductDetails API response:', data);

        if (!data || !data._id) {
          throw new Error('Invalid product response');
        }

        const imgs = Array.isArray(data.images)
          ? data.images
          : data.image
          ? [data.image]
          : [];

        setProduct(data);
        setImages(imgs);
        setActiveImage(imgs[0] || '');
      } catch (err) {
        console.error(err);
        setError('Unable to load product.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  /* ---------------- CART ---------------- */
  const isInCart = safeCart.some(i => i._id === product?._id);

  const handleAddToCart = () => {
    if (isInCart) {
      setShowModal(true);
      return;
    }
    addToCart(product);
  };

  /* ---------------- STATES ---------------- */
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  /* ---------------- DELIVERY DATE ---------------- */
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 3);

  return (
    <>
      <Container className="py-5">
        {/* BREADCRUMB */}
        <nav style={{ fontSize: 14, marginBottom: 24 }}>
          <Link to="/" className="text-muted text-decoration-none">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/products" className="text-muted text-decoration-none">Products</Link>
          <span className="mx-2">/</span>
          <span>{product.name}</span>
        </nav>

        <Row className="gy-5">
          {/* IMAGE GALLERY */}
          <Col md={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: 20 }}>
              <div className="p-4 text-center">
                {activeImage ? (
                  <img
                    src={`https://apimyshop.myidealtd.co.uk/uploads/${activeImage}`}
                    alt={product.name}
                    style={{
                      maxHeight: 420,
                      width: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div>No Image</div>
                )}
              </div>
            </Card>

            {/* THUMBNAILS */}
            {images.length > 1 && (
              <div className="d-flex justify-content-center gap-3 mt-3">
                {images.slice(0, 3).map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: 12,
                      border:
                        activeImage === img
                          ? '2px solid #000'
                          : '1px solid #ddd',
                      cursor: 'pointer',
                      padding: 6
                    }}
                  >
                    <img
                      src={`https://apimyshop.myidealtd.co.uk/uploads/${img}`}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Col>

          {/* PRODUCT INFO */}
          <Col md={6}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#0d6efd',
                background: '#e7f1ff',
                padding: '6px 14px',
                borderRadius: 999
              }}
            >
              {product.category}
            </span>

            <h1 className="mt-3 fw-bold">{product.name}</h1>

            <div className="fs-3 fw-bold text-success my-3">
              £{product.price.toFixed(2)}
            </div>

            <div className="text-muted d-flex align-items-center gap-2 mb-3">
              <FaTruck />
              Delivery by {deliveryDate.toDateString()}
            </div>

            <p className="text-secondary">
              {product.description || 'No description available.'}
            </p>

            <Button
              size="lg"
              className="mt-4 w-100"
              disabled={isInCart}
              onClick={handleAddToCart}
              style={{
                borderRadius: 14,
                background: isInCart ? '#198754' : '#000',
                border: 'none',
                fontWeight: 600
              }}
            >
              {isInCart ? 'Already in Cart' : 'Add to Cart'}
            </Button>
          </Col>
        </Row>
      </Container>

      {/* DUPLICATE MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="text-warning me-2" />
            Already in Cart
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          This product is already in your cart.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ProductDetails;
